/**
 * The adapter that makes the toolkit's persistence land on the user's account.
 *
 * `mountAppShell` writes the shell's own state — the theme, the appearance, which
 * sidebar sections are collapsed, and (the one LPN-1 cares about) where every window
 * sits — through a [Persister] the app supplies. Point that at `localStorage` and the
 * arrangement follows the *browser*; point it here and it follows the *account*,
 * which is what the ticket asks for.
 *
 * Deliberately almost nothing. Every decision it might have made — what is cached,
 * what is written through, what happens when a write fails, when the read is ready —
 * is made once in `StorageRepository`, where the mobile clients can reach it too. This
 * exists because :client must not depend on the toolkit (see the module note below),
 * not because there is logic here.
 *
 * The one exception is the theme selection, which is read *through*
 * [defaultedThemeSelectionJson] so that an account with nothing stored mounts on LunaPin's
 * own default rather than the toolkit's. That rule **used to live beside this file** and now
 * lives in `:client` — see the note below.
 *
 * ── Why this lives in :web, and what changed underneath it ───────────────────
 *
 * This file's header used to say that ":client is toolkit-free on purpose: it is shared with
 * Android and iOS, whose UIs are native and have no business linking a **web shell**." That
 * sentence was doing two jobs and only one of them survives.
 *
 * **It still holds for `lunula-web`.** This class implements `Persister` against a shell that
 * exists only in a browser, so the file that names both sides belongs here. Same boundary
 * Lunicle draws, same reason.
 *
 * **It never held for `lunula-core`**, and LPN-22 is where that mattered: the theme model is
 * full KMP with iOS targets, and a phone that is to look like the server it is connected to
 * needs it. So `:client` depends on `lunula-core` now, `DefaultLook` moved there, and the
 * claim above has been narrowed to what is true. A comment asserting a boundary the build no
 * longer draws is worse than no comment.
 */
package se.soderbjorn.lunapin.web

import se.soderbjorn.lunapin.client.StorageRepository
import se.soderbjorn.lunapin.client.theme.defaultedThemeSelectionJson
import se.soderbjorn.lunapin.clientserver.UiSettingKeys
import se.soderbjorn.lunula.core.Persister

/**
 * A [Persister] backed by the LunaPin server.
 *
 * @param storage the account's settings, cached and written through by
 *   [StorageRepository].
 */
class ServerPersister(
    private val storage: StorageRepository,
) : Persister {
    /**
     * Suspends until the account's settings have been fetched, then answers from the
     * cache.
     *
     * That the suspension is real matters: the shell reads several keys *while it
     * mounts*, so answering "nothing yet" and letting the real values arrive later
     * would paint an empty default workspace and then flinch to the user's own a
     * moment afterwards. `StorageRepository.setting` waits for the one fetch, so
     * every read after the first is immediate.
     *
     * The one key answered with anything other than the stored value is the theme
     * selection, which is filtered through [defaultedThemeSelectionJson] so an account
     * that has chosen no theme mounts on Citron instead of the toolkit's own default.
     * Here rather than anywhere else because this is the only place that sees both "what
     * is stored" and "what the shell is about to read", and it is a read-side substitution
     * with no write behind it — nobody's stored settings are touched, so the default stays
     * a default and is free to change later. See DefaultLook.kt, which owns the rule and
     * the two tiers it applies in.
     */
    override suspend fun read(key: String): String? {
        val stored = storage.setting(key)
        if (key != UiSettingKeys.THEME_SELECTION) return stored
        return defaultedThemeSelectionJson(stored)
    }

    /**
     * Caches [value] and sends it to the server.
     *
     * Never throws — see `StorageRepository.putSetting`, which swallows a failed
     * round-trip on purpose. This is called from the toolkit's paint path, and the
     * toolkit logs and rethrows what reaches it, so an exception here would surface
     * as a console error during a window drag and change nothing for the better.
     */
    override suspend fun write(key: String, value: String) = storage.putSetting(key, value)
}
